import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/GridLegacy';
// biome-ignore lint/correctness/noUnusedImports: Automated migration to Biome
import React from 'react';
import { setImgWidth } from '@/utils/frontify';
import type { Contentful } from '../../contentful/contentful-exports';
import useDeviceWidth from '../../hooks/deviceWidth';
import CTA, { type CTAContent } from '../Elements/CTA';
import type { ImageSectionContent } from '../ImageSection/ImageSection';
import RichText from '../RichText/RichText';
import useStyles from './OneThirdImageWithCopy.styles';

/**
 * @deprecated V2 component
 */
export interface OneThirdImageWithCopyContent extends Contentful {
  fields: {
    heading: string;
    body: RichTextDocument;
    image: ImageSectionContent;
    cta: CTAContent;
    imageAlignment: string;
    imageMobilePosition: string;
    altText?: string;
  };
}

interface ImageSectionProps {
  content: ImageSectionContent;
}

function ImageSection({ content }: ImageSectionProps) {
  const { classes } = useStyles();
  return (
    // biome-ignore lint/performance/noImgElement: Automated migration to Biome
    <img
      className={classes.imageSection}
      src={setImgWidth(content?.fields?.image?.[0]?.preview_url, 1440)}
      alt={content?.fields?.imageSectionAltText}
    />
  );
}

interface TextProps {
  content: {
    heading: string;
    body: RichTextDocument;
    cta: CTAContent;
  };
}

function TextSection({ content }: TextProps) {
  const { classes } = useStyles();

  const { heading, body, cta } = content;
  return (
    <div className={classes.textSection}>
      <h1 className={classes.heading}>{heading}</h1>
      <RichText title={heading} content={body} />
      {cta && <CTA content={cta.fields} />}
    </div>
  );
}

interface Props {
  content: OneThirdImageWithCopyContent;
}

export default function OneThirdImageWithCopy({ content }: Props) {
  const { classes } = useStyles();
  const { isMobile } = useDeviceWidth();

  const { heading, body, image, cta, imageAlignment, imageMobilePosition } =
    content.fields || {};

  const isMobileTop = isMobile && imageMobilePosition === 'top';
  const imageLeft = isMobileTop ? 12 : imageAlignment === 'left' ? 4 : 8;
  const imageRight = isMobileTop ? 12 : imageAlignment === 'right' ? 4 : 8;
  const containerClass = `${classes.container} ${
    isMobile && imageMobilePosition === 'top'
      ? imageAlignment === 'left'
        ? classes.imageTopPositionFromLeft
        : classes.imageTopPositionFromRight
      : null
  }`;

  return (
    <Grid container className={containerClass}>
      <Grid
        item
        className={classes.backgroundImageSection}
        xs={imageLeft}
        md={imageLeft}
      >
        {imageAlignment === 'left' ? (
          <ImageSection content={image as ImageSectionContent} />
        ) : (
          <TextSection content={{ heading, body, cta }} />
        )}
      </Grid>
      <Grid
        item
        className={classes.backgroundImageSection}
        xs={imageRight}
        md={imageRight}
      >
        {imageAlignment === 'right' ? (
          <ImageSection content={image as ImageSectionContent} />
        ) : (
          <TextSection content={{ heading, body, cta }} />
        )}
      </Grid>
    </Grid>
  );
}
